/**
 * src/components/Financial/modules/PyrapointModule.jsx
 * 【完全修正版：ダークモード対応・デザイン統一】
 */
import React, { useMemo } from "react";
import { LayoutGrid, Info } from "lucide-react";

/**
 * ピラポイントの計算ロジック (変更なし)
 */
export const calculatePyrapoint = function (displayData, stats, config) {
  if (!displayData || displayData.length === 0 || !stats) return null;

  var startPrice = parseFloat(config.startPrice) || 0;
  var startIndex = config.startIndex || 0;

  if (startPrice <= 0) {
    var maxP = -Infinity,
      minP = Infinity,
      maxI = 0,
      minI = 0,
      found = false;
    displayData.forEach(function (d, i) {
      var h = d.High !== null && d.High !== undefined ? d.High : d.Price;
      var l = d.Low !== null && d.Low !== undefined ? d.Low : d.Price;
      if (h !== null && h !== undefined) {
        found = true;
        if (h > maxP) {
          maxP = h;
          maxI = i;
        }
        if (l < minP) {
          minP = l;
          minI = i;
        }
      }
    });
    if (!found) return null;
    startPrice = config.isUpTrend ? minP : maxP;
    startIndex = config.isUpTrend ? minI : maxI;
  }

  var divisor = config.priceDivisor || 10;
  var normStartP = startPrice / divisor;
  var rootP = Math.sqrt(normStartP);
  var barsPerDeg = config.barsPerDeg || 1.0;
  var cycles = config.gridCycles || 2;
  var totalDegrees = cycles * 360;
  var squares = [];

  for (var i = 0; i < totalDegrees / 90; i++) {
    var degStart = i * 90;
    var degEnd = (i + 1) * 90;
    var t1 = startIndex + Math.round(degStart * barsPerDeg);
    var t2 = startIndex + Math.round(degEnd * barsPerDeg);

    for (var j = 0; j < totalDegrees / 90; j++) {
      var stepLow = j * 0.5;
      var stepHigh = (j + 1) * 0.5;
      var r_low, r_high;
      if (config.isUpTrend) {
        r_low = rootP + stepLow;
        r_high = rootP + stepHigh;
      } else {
        r_low = Math.max(0, rootP - stepLow);
        r_high = Math.max(0, rootP - stepHigh);
      }
      var p_low = Math.pow(r_low, 2) * divisor;
      var p_high = Math.pow(r_high, 2) * divisor;

      squares.push({
        t1: t1,
        t2: t2,
        p_low: p_low,
        p_high: p_high,
        isUp: config.isUpTrend,
      });
    }
  }

  return { squares: squares, startPrice: startPrice, startIndex: startIndex };
};

/**
 * ピラポイント描画レイヤー (フック順序修正・完全版)
 */
export const PyrapointLayer = function (props) {
  var show = props.show,
    displayData = props.displayData,
    stats = props.stats;
  var coords = props.coords,
    config = props.config,
    isDarkMode = props.isDarkMode;

  // 1. 計算ロジックのフック (最上部へ)
  var pyra = useMemo(
    function () {
      return calculatePyrapoint(displayData, stats, config);
    },
    [displayData, stats, config],
  );

  // 2. 垂直グリッド座標のフック (常に実行されるように配置)
  var crossTimes = useMemo(
    function () {
      if (!pyra || !pyra.squares) return [];
      var times = [];
      var seen = {};
      pyra.squares.forEach(function (sq) {
        var mid = (sq.t1 + sq.t2) / 2;
        [sq.t1, sq.t2, mid].forEach(function (t) {
          var tk = Math.round(t * 100) / 100;
          if (!seen[tk]) {
            times.push(t);
            seen[tk] = true;
          }
        });
      });
      return times;
    },
    [pyra],
  );

  // 3. 水平グリッド座標のフック (常に実行されるように配置)
  var crossPrices = useMemo(
    function () {
      if (!pyra || !pyra.squares) return [];
      var prices = [];
      var seen = {};
      pyra.squares.forEach(function (sq) {
        var pMid = (sq.p_low + sq.p_high) / 2;
        [sq.p_low, sq.p_high, pMid].forEach(function (p) {
          var pk = Math.round(p * 1000) / 1000;
          if (!seen[pk]) {
            prices.push(p);
            seen[pk] = true;
          }
        });
      });
      return prices;
    },
    [pyra],
  );

  // --- フックの定義がすべて終わった後に早期リターンを配置する ---
  if (!show || !pyra || !coords || !coords.getX || !coords.getY) {
    return null;
  }

  var getX = coords.getX,
    getY = coords.getY;

  var iH = coords.iH || 500;
  var padT = (coords.pad && coords.pad.t) || 80;
  var padL = (coords.pad && coords.pad.l) || 60;
  var actualWidth = coords.actualWidth || 1200;
  var padR = (coords.pad && coords.pad.r) || 80;

  var gridColor = isDarkMode ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1";
  var crossGridColor = isDarkMode
    ? "rgba(239, 68, 68, 0.25)"
    : "rgba(239, 68, 68, 0.15)";

  return (
    <g className="pyrapoint-layer">
      {/* 垂直グリッド */}
      {crossTimes.map(function (t, idx) {
        var xPos = getX(t);
        if (xPos < padL - 100 || xPos > actualWidth - padR + 100) return null;
        return (
          <line
            key={"v-grid-" + idx}
            x1={xPos}
            y1={padT}
            x2={xPos}
            y2={padT + iH}
            stroke={crossGridColor}
            strokeWidth="1"
            strokeDasharray="1,3"
          />
        );
      })}

      {/* 水平グリッド */}
      {crossPrices.map(function (p, idx) {
        var yPos = getY(p);
        if (isNaN(yPos) || yPos < padT - 50 || yPos > padT + iH + 50)
          return null;
        return (
          <line
            key={"h-grid-" + idx}
            x1={padL}
            y1={yPos}
            x2={actualWidth - padR}
            y2={yPos}
            stroke={crossGridColor}
            strokeWidth="1"
            strokeDasharray="1,3"
          />
        );
      })}

      {/* スクエアと対角線 */}
      {pyra.squares.map(function (sq, i) {
        var x1 = getX(sq.t1),
          x2 = getX(sq.t2);
        var y_low = getY(sq.p_low),
          y_high = getY(sq.p_high);
        if (x1 > 2000 || x2 < -100 || isNaN(y_low) || isNaN(y_high))
          return null;

        return (
          <g key={"pyra-sq-" + i}>
            <line
              x1={x1}
              y1={y_low}
              x2={x2}
              y2={y_low}
              stroke={gridColor}
              strokeWidth="0.5"
              strokeDasharray="2,2"
              opacity="0.4"
            />
            <line
              x1={x2}
              y1={y_low}
              x2={x2}
              y2={y_high}
              stroke={gridColor}
              strokeWidth="0.5"
              strokeDasharray="2,2"
              opacity="0.4"
            />
            <line
              x1={x1}
              y1={y_low}
              x2={x2}
              y2={y_high}
              stroke={sq.isUp ? "#3498db" : "#ef4444"}
              strokeWidth="1.3"
              opacity="0.3"
            />
            <line
              x1={x1}
              y1={y_high}
              x2={x2}
              y2={y_low}
              stroke={sq.isUp ? "#ef4444" : "#3498db"}
              strokeWidth="1.3"
              strokeDasharray="1,3"
              opacity="0.8"
            />
          </g>
        );
      })}

      {/* ベース価格表示 */}
      <circle
        cx={getX(pyra.startIndex)}
        cy={getY(pyra.startPrice)}
        r="4"
        fill={config.isUpTrend ? "#3498db" : "#ef4444"}
      />
      <text
        x={getX(pyra.startIndex)}
        y={getY(pyra.startPrice) - 15}
        fontSize="10"
        fontWeight="bold"
        fill={isDarkMode ? "#ffffff" : "#64748b"}
        textAnchor="middle"
        style={{
          paintOrder: "stroke",
          stroke: isDarkMode ? "#0b0e14" : "#ffffff",
          strokeWidth: "3px",
        }}
      >
        Pyra Base: {(pyra.startPrice || 0).toLocaleString()}
      </text>
    </g>
  );
};

/**
 * ピラポイント設定UI (他モジュールとデザイン統一)
 */
export const PyrapointSettingsUI = function (props) {
  var show = props.show,
    setShow = props.setShow,
    config = props.config,
    setConfig = props.setConfig;
  var isDarkMode = props.isDarkMode;

  // テーマ定義の統一
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
    inputBg: isDarkMode ? "#0b0e14" : "#ffffff",
    inputBorder: isDarkMode ? "#374151" : "#cbd5e1",
  };

  var update = function (key, val) {
    var newCfg = Object.assign({}, config);
    newCfg[key] = val;
    setConfig(newCfg);
  };

  /**
   * ボタン共通スタイル (f98.jpg に合わせた統一デザイン)
   */
  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  var inputStyle = {
    padding: "6px 10px",
    fontSize: "11px",
    borderRadius: "6px",
    border: "1px solid " + theme.inputBorder,
    background: theme.inputBg,
    color: theme.text,
    outline: "none",
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: theme.text,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <LayoutGrid size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <span>pyrapoint(ピラポイント)</span>
        </div>
        <button
          onClick={function () {
            setShow(!show);
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "25px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "11px",
                fontWeight: "bold",
                color: theme.subText,
              }}
            >
              Decimal Shift (Divisor)
            </label>
            <select
              value={config.priceDivisor}
              onChange={function (e) {
                update("priceDivisor", parseFloat(e.target.value));
              }}
              style={inputStyle}
            >
              <option value={0.1}>0.1 (Gold/Forex)</option>
              <option value={1}>1.0 (Stocks)</option>
              <option value={10}>10.0 (Crypto Low)</option>
              <option value={100}>100.0 (Crypto High)</option>
              <option value={1000}>1000.0 (BTC)</option>
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "11px",
                fontWeight: "bold",
                color: theme.subText,
              }}
            >
              トレンド方向
            </label>
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={function () {
                  update("isUpTrend", true);
                }}
                style={btnSt(config.isUpTrend)}
              >
                上昇
              </button>
              <button
                onClick={function () {
                  update("isUpTrend", false);
                }}
                style={btnSt(!config.isUpTrend)}
              >
                下降
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "11px",
                fontWeight: "bold",
                color: theme.subText,
              }}
            >
              Bars per 90°
            </label>
            <input
              type="number"
              value={config.barsPerDeg * 90}
              onChange={function (e) {
                update("barsPerDeg", parseFloat(e.target.value) / 90);
              }}
              style={Object.assign({}, inputStyle, { width: "80px" })}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "11px",
                fontWeight: "bold",
                color: theme.subText,
              }}
            >
              表示サイクル数
            </label>
            <input
              type="number"
              value={config.gridCycles}
              onChange={function (e) {
                update("gridCycles", parseInt(e.target.value));
              }}
              style={Object.assign({}, inputStyle, { width: "60px" })}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: theme.subText,
            fontSize: "11px",
            borderTop:
              "1px solid " +
              (isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9"),
            paddingTop: "15px",
          }}
        >
          <Info size={14} />
          <span>
            価格の平方根に基づく90度サイクルを描画。対角線は時間と価格の均衡点を示します。
          </span>
        </div>
      </div>
    </div>
  );
};
